import React, { useState, useContext } from "react";
import ModalWrapper from "@/components/modals/ModalWrapper";
import ConfirmationModal from "@/components/modals/ConfirmationModal";
import CreateNotificationModal from "@/components/modals/schedulePush/CreateNotificationModal";
import { IConfirmationModal } from "@/interfaces/IConfirmationModal";
import useHTTP from "../../hooks/useHTTP";
import { ICreateSchedulePush } from "@/interfaces/ScheduledNotifications";
import { SchedulePushNotificationContext } from "@/context/SchedulePushNotificationContext";
import "../../../css/confirmationModal.scss";
import { ToastMessageType } from "../global/ToastNotification";

const UpcomingScheduledNotification = ({
  contentId,
  notificationId,
  publishDetails,
  scheduledPushTime,
  setToastVariables,
  setShowToast,
}) => {
  const { clearStore } = useContext(SchedulePushNotificationContext);
  const { put, remove } = useHTTP();
  const [openEditNotificationModal, setEditNotificationModal] =
    useState<boolean>(false);
  const [openUnscheduleNotificationModal, setUnscheduleNotificationModal] =
    useState<boolean>(false);
  const [openContinueEditingModal, setContinueEditingModal] =
    useState<boolean>(false);
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23"
  };
  let formattedPushDateTime = scheduledPushTime
    ? new Intl.DateTimeFormat(undefined, options)
        .format(new Date(scheduledPushTime))
        .replace(" at", ", ")
    : undefined;

  const pos = formattedPushDateTime.lastIndexOf(" ");
  formattedPushDateTime =
    formattedPushDateTime.substring(0, pos) +
    formattedPushDateTime.substring(pos + 1);

  const isWithScheduledPublish =
    (new Date(publishDetails.scheduledPublishDate).getTime() === new Date(scheduledPushTime).getTime());
  const [confirmationModalVariables, setConfirmationModalVariables] =
    useState<IConfirmationModal>({
      notificationId: null,
      modalClassName: "",
      modalTitle: "",
      confirmationQuestion: "",
      leftButton: "",
      rightButton: "",
    });

  const handleEditNotificationOpenModal = () => {
    setEditNotificationModal(true);
  };

  const [isExitingEditWithoutSave, setIsExitingEditWithoutSave] =
    useState<boolean>(false);

  const handleUnscheduleNotificationOpenModal = () => {
    setConfirmationModalVariables({
      notificationId: notificationId,
      modalClassName: `Unschedule`,
      modalTitle: `Unschedule Notification`,
      confirmationQuestion: `Are you sure you want to remove the notification scheduled for ${formattedPushDateTime}?`,
      leftButton: `Cancel`,
      rightButton: `Yes, Unschedule`,
    });
    setUnscheduleNotificationModal(true);
  };

  const handleExitCreateNotificationModal = () => {
    if (isExitingEditWithoutSave) {
      setConfirmationModalVariables({
        notificationId: notificationId,
        modalClassName: `continue-editing`,
        modalTitle: `Continue Editing?`,
        confirmationQuestion: `Do you want to continue editing or disregard any unsaved changes?`,
        leftButton: `Yes, cancel`,
        rightButton: `Continue Editing`,
      });
      setContinueEditingModal(true);
    } else {
      clearStore();
      setEditNotificationModal(false);
    }
  };

  const onEditNotificationClick = (schedulePush: ICreateSchedulePush) => {
    put(`notifications/notification/${notificationId}`, schedulePush)
      .then(() => {
        setToastVariables({
          messageType: ToastMessageType.SUCCESS,
          message: "Notification successfully updated.",
        });
        setShowToast(true);
      })
      .catch((err) => {
        console.log(err);
        setToastVariables({
          messageType: ToastMessageType.WARNING,
          message: "Something went wrong. Please try again.",
        });
        setShowToast(true);
      })
      .finally(() => {
        setEditNotificationModal(false);
      });
  };

  const unscheduleNotificationConfirmClick = () => {
    remove(`notifications/notification/${notificationId}`, {})
      .then(() => {
        setToastVariables({
          messageType: ToastMessageType.SUCCESS,
          message: "Notification successfully unscheduled.",
        });
        setShowToast(true);
      })
      .catch((err) => {
        console.log(err);
        setToastVariables({
          messageType: ToastMessageType.WARNING,
          message: "Something went wrong. Please try again.",
        });
        setShowToast(true);
      })
      .finally(() => setUnscheduleNotificationModal(false));
  };

  const onRequestCancelClick = () => {
    onRequestCloseClick();
    setEditNotificationModal(false);
    setContinueEditingModal(false);
    clearStore();
  };

  const onRequestCloseClick = () => {
    setUnscheduleNotificationModal(false);
    setContinueEditingModal(false);
  };

  return (
    <div className="notification-container">
      <div className="notification-title">
        <p className="title">Scheduled Notification</p>
      </div>

      {isWithScheduledPublish && (
        <p className="scheduled-publish-type-heading">With Scheduled Publish</p>
      )}
      <p className="date-heading">{formattedPushDateTime}</p>

      <div className="button-container">
        <button
          className="edit-notification-button"
          aria-label="Edit this Notification"
          disabled={!notificationId}
          onClick={handleEditNotificationOpenModal}
        >
          Edit
        </button>
        <ModalWrapper
          isOpen={openEditNotificationModal}
          onRequestClose={handleExitCreateNotificationModal}
          className="create-notification-modal"
        >
          <CreateNotificationModal
            contentId={contentId}
            publishDetails={publishDetails}
            updateNotificationId={notificationId}
            onCreateNotificationClick={onEditNotificationClick}
            setIsMidEditNotification={setIsExitingEditWithoutSave}
          />
        </ModalWrapper>

        <ModalWrapper
          isOpen={openContinueEditingModal}
          onRequestClose={onRequestCloseClick}
          className="view-continue-editing-modal"
        >
          <ConfirmationModal
            confirmationModalVariables={confirmationModalVariables}
            onLeftButtonClick={onRequestCancelClick}
            onRightButtonClick={onRequestCloseClick}
            modalClassName={confirmationModalVariables.modalClassName}
          />
        </ModalWrapper>

        <button
          className="unschedule-notification-button"
          aria-label="Delete and unschedule this notification"
          onClick={handleUnscheduleNotificationOpenModal}
          disabled={!notificationId}
        >
          Unschedule
        </button>
      </div>
      <ModalWrapper
        isOpen={openUnscheduleNotificationModal}
        onRequestClose={onRequestCloseClick}
        className="view-unschedule-notification-modal"
      >
        <ConfirmationModal
          confirmationModalVariables={confirmationModalVariables}
          onLeftButtonClick={onRequestCloseClick}
          onRightButtonClick={unscheduleNotificationConfirmClick}
          modalClassName={confirmationModalVariables.modalClassName}
        />
      </ModalWrapper>
    </div>
  );
};

export default UpcomingScheduledNotification;
